import { Switch } from '@headlessui/react';
import type { ReactElement } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

const toggleSwitchVariants = tv({
  slots: {
    leftLabel: 'rounded-full px-6 py-2 text-sm transition-colors duration-200',
    rightLabel: 'rounded-full px-6 py-2 text-sm transition-colors duration-200',
    switch:
      'group relative mx-0.5 inline-flex items-center rounded-full bg-gray-300 transition-colors duration-200',
    thumb:
      'inline-block rounded-full bg-white shadow transition-transform duration-200 ease-in-out',
  },
  variants: {
    isActive: {
      true: {
        leftLabel: 'text-gray-500',
        rightLabel:
          'border border-rw-main bg-white font-medium text-rw-main shadow-sm',
        switch: 'bg-rw-main',
      },
      false: {
        leftLabel:
          'border border-rw-main bg-white font-medium text-rw-main shadow-sm',
        rightLabel: 'text-gray-500',
        switch: 'bg-gray-300',
      },
    },
    disabled: {
      true: {
        leftLabel: 'opacity-50',
        rightLabel: 'opacity-50',
        switch: 'cursor-not-allowed opacity-50',
      },
    },
    size: {
      sm: {
        switch: 'h-4 w-7',
        thumb: 'size-2.5 translate-x-1 group-data-[checked]:translate-x-3.5',
      },
      md: {
        switch: 'h-6 w-11',
        thumb: 'size-4 translate-x-1 group-data-[checked]:translate-x-6',
      },
    },
  },
  defaultVariants: {
    isActive: false,
    disabled: false,
    size: 'md',
  },
});

type ToggleSwitchProps = VariantProps<typeof toggleSwitchVariants> & {
  onToggle: (checked: boolean) => void;
  labels?: [string, string];
  disabled?: boolean;
  checked?: boolean;
  size?: 'sm' | 'md';
};

const ToggleButton = ({
  checked = false,
  onToggle,
  disabled,
  size = 'md',
}: Pick<
  ToggleSwitchProps,
  'checked' | 'onToggle' | 'disabled' | 'size'
>): ReactElement => {
  const { switch: switchClass, thumb } = toggleSwitchVariants({
    isActive: checked,
    disabled,
    size,
  });

  return (
    <Switch
      checked={checked}
      onChange={onToggle}
      disabled={disabled}
      className={switchClass()}
    >
      <span className="sr-only">{checked ? 'Turn off' : 'Turn on'}</span>
      <span className={thumb()} />
    </Switch>
  );
};

export const ToggleSwitch = ({
  checked = false,
  onToggle,
  labels,
  disabled = false,
  size = 'md',
}: ToggleSwitchProps): ReactElement => {
  // ラベルがない場合は単純なトグルスイッチ
  if (!labels) {
    return (
      <ToggleButton
        checked={checked}
        onToggle={onToggle}
        disabled={disabled}
        size={size}
      />
    );
  }

  const { leftLabel, rightLabel } = toggleSwitchVariants({
    isActive: checked,
    disabled,
    size,
  });

  return (
    <div className="mb-8">
      <div className="flex items-center justify-center">
        <div className="inline-flex rounded-full border border-gray-200 bg-gray-100 p-0.5 shadow-sm">
          <div className="flex items-center space-x-3">
            <span className={leftLabel()}>{labels[0]}</span>
            <ToggleButton
              checked={checked}
              onToggle={onToggle}
              disabled={disabled}
              size={size}
            />
            <span className={rightLabel()}>{labels[1]}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
